import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
  memo,
} from "react";
import {
  Button,
  Select,
  DatePicker,
  Row,
  Col,
  Card,
  Empty,
  Pagination,
  Tooltip,
  Image,
  Drawer,
  Spin,
  Avatar,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  CalendarOutlined,
  FilterOutlined,
  MenuOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import ReportModal from "./ReportModal";
import dayjs from "dayjs";
import drEasyApi from "../../../Api/Customapi";
import { useDoctorQuery } from "../../../hooks/useDoctorQuery";
import {
  FaHistory,
  FaRegFile,
  FaSyncAlt,
  FaCalendarAlt,
  FaUserMd,
  FaClinicMedical,
  FaInfoCircle,
  FaTimes,
  FaClock,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { FaCircleXmark, FaUserDoctor } from "react-icons/fa6";
import { motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";
import CancelBookingModal from "../../Modals/CancelAppoinmentModal";
import { getImg } from "../../../Api/getImg";
import { useLanguage } from "../../../context/LanguageContext";
import "dayjs/locale/ar";
import "dayjs/locale/en";
import i18n from "../../../i18n";
import { formatTime } from "../../../utils/helpers";
const { RangePicker } = DatePicker;
const { Option } = Select;

const statusConfig = {
  Pending: {
    color: "bg-gradient-to-r from-amber-400 to-orange-500",
    textColor: "text-yellow-400",
    icon: "⏱️",
    bgLight: "bg-amber-50",
    borderColor: "border-amber-200",
  },
  Cancelled: {
    color: "bg-gradient-to-r from-red-400 to-red-600",
    textColor: "text-red-400",
    icon: "❌",
    bgLight: "bg-red-50",
    borderColor: "border-red-200",
  },
  Completed: {
    color: "bg-gradient-to-r from-green-400 to-emerald-500",
    textColor: "text-green-400",
    icon: "✅",
    bgLight: "bg-green-50",
    borderColor: "border-green-200",
  },
};

const useResponsive = () => {
  const [breakpoint, setBreakpoint] = useState("lg");
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setBreakpoint("sm");
        setIsMobile(true);
        setIsTablet(false);
      } else if (width < 768) {
        setBreakpoint("md");
        setIsMobile(false);
        setIsTablet(true);
      } else if (width < 1024) {
        setBreakpoint("lg");
        setIsMobile(false);
        setIsTablet(true);
      } else if (width < 1280) {
        setBreakpoint("xl");
        setIsMobile(false);
        setIsTablet(false);
      } else {
        setBreakpoint("2xl");
        setIsMobile(false);
        setIsTablet(false);
      }
    };

    updateBreakpoint();
    window.addEventListener("resize", updateBreakpoint);
    return () => window.removeEventListener("resize", updateBreakpoint);
  }, []);

  return { breakpoint, isMobile, isTablet };
};

const MyBookingCard = memo(
  ({
    booking,
    index,
    onViewReport,
    onCancel,
    bookingsWithNotes,
    isMobile,
    t,
  }) => {
    const status = statusConfig[booking.status] || statusConfig.Pending;

    const formatDate = (date) => {
      return dayjs(date)
        .locale(i18n.language)
        .format(isMobile ? "MMM D" : "MMM D, YYYY");
    };
    const handleViewReport = useCallback(() => {
      onViewReport(booking);
    }, [booking, onViewReport]);

    const handleCancel = useCallback(() => {
      onCancel(booking);
    }, [booking, onCancel]);
    const { isRTL } = useLanguage();

    return (
      <div
        className={`group relative bg-white rounded-xl sm:rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border ${
          status.borderColor
        } overflow-hidden ${isMobile ? "mb-4" : ""}`}
        style={{
          animationDelay: `${index * 100}ms`,
          animation: "fadeInUp 0.6s ease-out forwards",
        }}
      >
        {/* Status Badge */}
        <div
          className={`absolute  top-2 ${
            isRTL ? "left-2" : "right-2"
          } sm:top-4 sm:${
            isRTL ? "left-4" : "right-4"
          }   px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs font-bold ${
            status.bgLight
          } border ${status.borderColor} ${
            status.textColor
          } shadow-lg transform ${
            isMobile ? "rotate-0" : "rotate-3 group-hover:rotate-0"
          } transition-transform duration-300`}
        >
          {status.icon} {t(`myBookings.status.${booking.status.toLowerCase()}`)}
        </div>

        <div className="p-4 sm:p-6">
          <div
            className={`flex items-center gap-2  mb-3 sm:mb-4 ${
              isMobile ? "flex-wrap" : ""
            }`}
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-primary to-primary_hover rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-lg flex-shrink-0">
              {booking.doctorProfile ? (
                <Image
                  src={getImg(booking.doctorProfile)}
                  className="rounded-full border-primary w-full h-full object-cover"
                  fallback="/anonymous.png"
                />
              ) : (
                <span className="flex justify-center items-center w-full h-full">
<FaUserDoctor />
                </span>
              )}
            </div>
            <div className="ml-3 sm:ml-4 min-w-0 flex-1">
              <h3
                className={`min-w-0 max-w-52 font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300
  ${isMobile ? "text-base truncate" : "text-lg truncate"}
  ${isRTL ? "text-right" : "text-left"}`}
              >
                {t("myBookings.card.dr")} {booking.doctorName || t("booking.generalAppointment")}
              </h3>
              <div className="flex items-center text-xs sm:text-sm text-gray-500">
                <FaMapMarkerAlt className="mr-1 flex-shrink-0" />
                <span className={isMobile ? "truncate" : ""}>
                  {booking.clinicName}
                </span>
              </div>
            </div>
          </div>

          <div
            className={`p-3 sm:p-4 rounded-lg sm:rounded-xl mb-3 sm:mb-4 ${status.bgLight} border ${status.borderColor}`}
          >
            <div
              className={`${
                isMobile ? "space-y-2" : "flex items-center justify-between"
              }`}
            >
              <div>
                <div className="text-xs sm:text-sm font-semibold text-gray-700">
                  📅 {formatDate(booking.bookingDate)}
                </div>
                <div className="flex items-center text-xs sm:text-sm text-gray-600 mt-1 gap-2 ">
                  <FaClock className="mr-2 flex-shrink-0" />
                  <span className={isMobile ? "text-xs" : ""}>
                    {formatTime(booking.startTime)} -{" "}
                    {formatTime(booking.endTime)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`flex gap-3 items-center justify-between pt-3 sm:pt-4 border-t border-gray-100 ${
              isMobile ? "flex-wrap gap-2" : ""
            }`}
          >
            <div
              className={`flex ${isMobile ? "gap-1" : "gap-2 space-x-2"} ${
                isMobile ? "flex-wrap" : ""
              }`}
            >
              {booking.id && bookingsWithNotes[booking.id] ? (
                <Tooltip title={t("myBookings.card.viewReport")}>
                  <Button
                    type=""
                    icon={<FaRegFile />}
                    onClick={handleViewReport}
                    size={isMobile ? "small" : "middle"}
                    className="bg-blue-500 border-none text-white rounded-full hover:bg-blue-600 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                  />
                </Tooltip>
              ) : (
                <Tooltip title={t("myBookings.card.noReport")}>
                  <Button
                    type=""
                    icon={<FaInfoCircle />}
                    disabled
                    size={isMobile ? "small" : "middle"}
                    className="bg-gray-300 border-none text-gray-500 rounded-full flex items-center justify-center"
                  />
                </Tooltip>
              )}

              {booking.status === "Pending" && (
                <Tooltip title={t("myBookings.card.cancelBooking")}>
                  <Button
                    type=""
                    icon={<CloseOutlined className="text-lg fot-bold" />}
                    onClick={handleCancel}
                    size={isMobile ? "small" : "middle"}
                    className="bg-red-500 border-none text-white rounded-full hover:bg-red-600 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300"
                  />
                </Tooltip>
              )}
            </div>

            <div
              className={`text-xs text-gray-400 ${
                isMobile ? "mt-2 w-full text-center" : "ps-2"
              }`}
            >
              {t("myBookings.card.id")}: #{booking.bookingCode}
            </div>
          </div>
        </div>

        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-xl sm:rounded-2xl"></div>
      </div>
    );
  },
);

export default function MyBookings({ isProfile = false }) {
  const { t, isRTL } = useLanguage();
  const containerRef = useRef(null);
  const { breakpoint, isMobile, isTablet } = useResponsive();

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [bookingsWithNotes, setBookingsWithNotes] = useState({});
  const [filtersDrawerVisible, setFiltersDrawerVisible] = useState(false);
  const { data: doctorData } = useDoctorQuery();

  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(isMobile ? 4 : isTablet ? 6 : 12);
  const [clinicId, setClinicId] = useState(null);
  const [status, setStatus] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    const newPageSize = isMobile ? 4 : isTablet ? 6 : 12;
    setPageSize(newPageSize);
  }, [isMobile, isTablet]);

  const getGridCols = useMemo(() => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    if (breakpoint === "xl") return 3;
    return 4;
  }, [isMobile, isTablet, breakpoint]);

  const clinics = useMemo(() => {
    if (!doctorData || !doctorData.clinics)
      return [{ id: null, name: t("myBookings.filters.allClinics") }];
    return [
      { id: null, name: t("myBookings.filters.allClinics") },
      ...doctorData.clinics.map((c) => ({ id: c.id, name: c.name })),
    ];
  }, [doctorData, t]);

  const checkHasNotesForBooking = async (reportId, bookingId) => {
    try {
      const res = await drEasyApi.get(`/api/Reports/GetMyReport`);
      const row = res.data?.rows?.find((r) => r.bookingId === bookingId);
      if (!row) return false;

      return row.cells?.some(
        (cell) => (cell.text && cell.text.trim() !== "") || cell.photoId,
      );
    } catch (err) {
      console.error("Error checking notes for booking:", err);
      return false;
    }
  };

  // Handle cancel booking success
  const handleCancelSuccess = useCallback((cancelledBookingId) => {
    setBookings((prevBookings) =>
      prevBookings.map((booking) =>
        booking.id === cancelledBookingId
          ? { ...booking, status: "Cancelled" }
          : booking,
      ),
    );
  }, []);

  const openCancelModal = useCallback((booking) => {
    setBookingToCancel(booking);
    setCancelModalVisible(true);
  }, []);

  const closeCancelModal = useCallback(() => {
    setCancelModalVisible(false);
    setBookingToCancel(null);
  }, []);

  const handleViewReport = useCallback((booking) => {
    setSelectedReportId(booking.reportId);
    setSelectedBookingId(booking.id);
    setModalVisible(true);
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      const headerHeight = 80;
      const elementTop =
        containerRef.current.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementTop - headerHeight,
        behavior: "smooth",
      });
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [pageNumber, pageSize, clinicId, status, dateRange, isRTL]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = {
        PageNumber: pageNumber,
        PageSize: pageSize,
      };
      if (clinicId) params.ClinicId = clinicId;
      if (status !== null) params.Status = status;
      if (dateRange.length === 2) {
        params.FromDate = dayjs(dateRange[0]).format("YYYY-MM-DD");
        params.ToDate = dayjs(dateRange[1]).format("YYYY-MM-DD");
      }

      const response = await drEasyApi.get("/api/Book/getMyBooks", { params });

      setBookings(response.data.bookings || []);
      setTotalItems(response.data.totalCount || 0);

      const notesMap = {};
      for (const booking of response.data.bookings || []) {
        if (booking.id) {
          const hasNotes = await checkHasNotesForBooking(
            booking.reportId,
            booking.id,
          );
          notesMap[booking.id] = hasNotes;
        }
      }
      setBookingsWithNotes(notesMap);
    } catch (error) {
      console.error("Failed to fetch bookings", error);
    }
    setLoading(false);
  };

  const handlePageChange = useCallback((page, pageSize) => {
    setPageNumber(page);
    setPageSize(pageSize);
  }, []);

  const closeReport = useCallback(() => {
    setModalVisible(false);
    setSelectedReportId(null);
  }, []);

  const resetFilters = useCallback(() => {
    setClinicId(null);
    setStatus(null);
    setDateRange([]);
    setPageNumber(1);
    setPageSize(isMobile ? 4 : isTablet ? 6 : 12);
    if (isMobile) {
      setFiltersDrawerVisible(false);
    }
  }, [isMobile, isTablet]);
  const dateFormat = isRTL ? "D MMM، YYYY" : "MMM D, YYYY";

  // Filters component
  const FiltersContent = useMemo(
    () => (
      <div className="space-y-2">
        <div className="flex items-center justify-between mb-4">
          <h3
            className={`font-semibold text-gray-800 flex items-center m-0 ${
              isMobile ? "text-sm" : "text-md"
            }`}
          >
            <FilterOutlined className="mr-2 text-sm" />
            {t("myBookings.filters.title")}
          </h3>
          {isMobile && (
            <Button
              icon={<MenuOutlined />}
              onClick={() => setFiltersDrawerVisible(false)}
              type="text"
              size="small"
            />
          )}
        </div>

        <div
          className={`grid grid-cols-1 ${
            isMobile
              ? "gap-3"
              : isTablet
                ? "md:grid-cols-2 gap-4"
                : "lg:grid-cols-4 gap-4"
          }`}
        >
          <Select
            allowClear
            placeholder={t("myBookings.filters.selectClinic")}
            className="w-full"
            value={clinicId}
            onChange={(val) => {
              setPageNumber(1);
              setClinicId(val);
            }}
            size={isMobile ? "middle" : "large"}
          >
            {clinics.map((c) => (
              <Option key={c.id || "all"} value={c.id}>
                {c.name}
              </Option>
            ))}
          </Select>

          <Select
            allowClear
            placeholder={t("myBookings.filters.selectStatus")}
            className="w-full"
            value={status}
            onChange={(val) => {
              setPageNumber(1);
              setStatus(val);
            }}
            size={isMobile ? "middle" : "large"}
          >
            <Option value="Pending">{t("myBookings.status.pending")}</Option>
            <Option value="Completed">
              {t("myBookings.status.completed")}
            </Option>
            <Option value="Cancelled">
              {t("myBookings.status.cancelled")}
            </Option>
          </Select>

          <RangePicker
            className="w-full"
            value={dateRange}
            onChange={(dates) => {
              setPageNumber(1);
              setDateRange(dates || []);
            }}
            allowEmpty={[true, true]}
            format={dateFormat}
            suffixIcon={<CalendarOutlined />}
            size={isMobile ? "middle" : "large"}
            placeholder={
              isRTL
                ? ["تاريخ البداية", "تاريخ النهاية"]
                : ["Start Date", "End Date"]
            }
          />

          <Button
            icon={<ReloadOutlined />}
            onClick={resetFilters}
            size={isMobile ? "middle" : "large"}
            className="rounded-xl bg-primary border-none text-white hover:bg-primary_hover transform hover:scale-105 transition-all duration-300"
            block
          >
            {isMobile
              ? t("myBookings.filters.reset")
              : t("myBookings.filters.resetFilters")}
          </Button>
        </div>
      </div>
    ),
    [clinics, clinicId, status, dateRange, resetFilters, isMobile, isTablet, t],
  );

  // Render filters
  const renderFilters = useMemo(() => {
    if (isMobile) {
      return (
        <>
          <div className="mb-4 flex justify-between items-center">
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFiltersDrawerVisible(true)}
              className="rounded-xl bg-primary border-none text-white"
            >
              {t("myBookings.filters.filters")}
            </Button>
          </div>

          <Drawer
            title={t("myBookings.filters.title")}
            placement="bottom"
            closable={true}
            onClose={() => setFiltersDrawerVisible(false)}
            open={filtersDrawerVisible}
            height="auto"
            className="mobile-filters-drawer"
          >
            {FiltersContent}
          </Drawer>
        </>
      );
    }

    return (
      <Card className="mb-6 sm:mb-8 shadow-sm border-0 bg-white/80 backdrop-blur-sm p-3 sm:p-0">
        {FiltersContent}
      </Card>
    );
  }, [isMobile, FiltersContent, filtersDrawerVisible, t]);

  return (
    <motion.div
      ref={containerRef}
      className="min-h-screen"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
    >
      {/* Add custom CSS for animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .ant-pagination-item {
          margin: ${isMobile ? "0 2px" : "0 4px"} !important;
        }

        .mobile-filters-drawer .ant-drawer-body {
          padding: 16px !important;
        }

        @media (max-width: 640px) {
          .ant-card-body {
            padding: 12px !important;
          }
        }
      `}</style>
      <div className={`${isMobile ? "p-4" : "p-6"}`}>
        <div className={isMobile ? "mb-4" : "mb-6"}>
          {!isProfile && (
            <div className="mb-6 text-center md:text-left flex flex-col items-center">
              <h1 className="text-2xl md:text-3xl font-semibold text-textPrimary flex items-center justify-center md:justify-start gap-3">
                <FaHistory />
                {t("myBookings.title")}
              </h1>
              <p className="mt-3 text-textPrimary/70 max-w-2xl mx-auto">
                {t("myBookings.subtitle")}
              </p>
            </div>
          )}
        </div>

        {/* Filters */}
        {renderFilters}

        {/* Bookings Content */}
        <Spin spinning={loading}>
          {bookings.length > 0 ? (
            <>
              <div
                className={`${isProfile ? "" : "px-20"} grid grid-cols-1 ${
                  isMobile
                    ? "gap-4"
                    : isTablet || isProfile
                      ? "sm:grid-cols-2 gap-4 sm:gap-6"
                      : "sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6"
                } mb-6 sm:mb-8`}
              >
                {bookings.map((booking, index) => (
                  <MyBookingCard
                    key={booking.id}
                    booking={booking}
                    index={index}
                    onViewReport={handleViewReport}
                    onCancel={openCancelModal}
                    bookingsWithNotes={bookingsWithNotes}
                    isMobile={isMobile}
                    t={t}
                  />
                ))}
              </div>

              {/* Pagination */}
              <div className="flex justify-center">
                <div
                  className={`bg-white rounded-xl sm:rounded-2xl shadow-lg ${
                    isMobile ? "p-2" : "p-4"
                  }`}
                >
                  <Pagination
                    current={pageNumber}
                    pageSize={pageSize}
                    total={totalItems}
                    onChange={handlePageChange}
                    showTotal={(total, range) => (
                      <span
                        className={`text-gray-600 font-medium ${
                          isMobile ? "text-xs" : ""
                        }`}
                      >
                        {isMobile
                          ? `${total} ${t("myBookings.pagination.total")}`
                          : t("myBookings.pagination.totalAppointments", {
                              total,
                            })}
                      </span>
                    )}
                    size={isMobile ? "small" : "default"}
                    showSizeChanger={!isMobile}
                    showQuickJumper={!isMobile}
                    className="modern-pagination"
                  />
                </div>
              </div>
            </>
          ) : (
            !loading && (
              <div className="text-center py-12 sm:py-16">
                <div className={`${isMobile ? "text-4xl" : "text-6xl"} mb-4`}>
                  📅
                </div>
                <h3
                  className={`font-bold text-gray-600 mb-2 ${
                    isMobile ? "text-lg" : "text-2xl"
                  }`}
                >
                  {t("myBookings.empty.title")}
                </h3>
                <p className={`text-gray-500 ${isMobile ? "text-sm" : ""}`}>
                  {t("myBookings.empty.description")}
                </p>
              </div>
            )
          )}
        </Spin>
      </div>

      {/* Report Modal */}
      <ReportModal
        visible={modalVisible}
        onCancel={closeReport}
        reportId={selectedReportId}
        bookingId={selectedBookingId}
      />

      {/* Cancel Booking Modal */}
      <CancelBookingModal
        visible={cancelModalVisible}
        onCancel={closeCancelModal}
        booking={bookingToCancel}
        onSuccess={handleCancelSuccess}
      />
    </motion.div>
  );
}

MyBookingCard.displayName = "MyBookingCard";
